/**
 * parent-field-values — wartości pól rekordu-RODZICA (kartoteki) dla wiązań `parent_field`
 * parametrów słowników w polach wierszy tabel dodatkowych.
 *
 * Wiązanie `parent_field` czyta wartość parametru słownika z pola kartoteki, pod którą żyje
 * tabela dodatkowa — TAK, JAK JEST NA EKRANIE (także niezapisane zmiany). Front wysyła do
 * serwera wyłącznie te pola rodzica, które wskazują wiązania pól danej tabeli, i wyłącznie
 * jako wartości skalarne (string/number/boolean/null): tablica lub obiekt nigdy nie trafia do
 * żądania (serwer odrzuca cały zapis kartoteki z nieskalarną wartością) — takie wartości
 * zamieniamy na `null`, czyli „brak wartości" (ta sama koercja co w `record-values.ts`).
 *
 * Dwa miejsca użycia:
 *  - lista opcji słownika przy otwarciu kontrolki (`useFieldSelectOptions`) — pola jednego pola
 *    wiersza, body `POST /dictionaries/:code/options` (`parentValues` + `parentKey`);
 *  - zapis całej kartoteki (`useAdditionalTableDeltas.buildAdditionalParentValues`) — suma pól
 *    z tabel, w których są oczekujące zmiany wierszy (`additionalParentValues` w body).
 */
import {
  type DictionaryParamBindingsMap,
  normalizeDictionaryParamBinding,
  type ParentFieldBindingKey,
} from '@/hooks/useDictionary';
import { toAdditionalViewRecordValue } from '@/lib/additional-views/record-values';
import { api } from '@/lib/api';

import type { AdditionalField } from './types';

/** Wartość pola rodzica w żądaniu — wyłącznie skalar albo `null` (brak wartości). */
export type ParentScalarValue = string | number | boolean | null;

/** Mapa slug pola rodzica → wartość skalarna (kształt `parentValues` / `additionalParentValues`). */
export type AdditionalParentValues = Record<string, ParentScalarValue>;

/**
 * Klucz cache definicji pól tabeli dodatkowej — jedno źródło dla zakładki na karcie rekordu,
 * zapisu strony, edytora układu kolumn w kreatorze i Ustawień → Tabele dodatkowe (te po
 * dodaniu, zmianie i usunięciu pola odświeżają go, więc otwarte karty i kreator widzą zmianę).
 */
export function additionalFieldsKey(tableId: string) {
  return ['additional-fields', tableId] as const;
}

/**
 * Prefiks kluczy cache zapamiętanych WIERSZY tabeli dodatkowej — wszystkie rekordy-rodzice
 * (`['additional-records', tableId, parentId]`, patrz `additionalRecordsKey` w `AdditionalTableTab`).
 * Unieważnienie po prefiksie odświeża wiersze każdej otwartej karty tej tabeli; używa go m.in.
 * Ustawienia → Tabele dodatkowe po zmianie lub usunięciu pola (wiersze zawierają jego wartości).
 */
export function additionalRecordsTableKey(tableId: string) {
  return ['additional-records', tableId] as const;
}

/** Pobiera definicje pól tabeli dodatkowej (`GET /settings/additional-tables/:id/fields`). */
export async function fetchAdditionalTableFields(tableId: string): Promise<AdditionalField[]> {
  const res = await api.get<{ fields: AdditionalField[] }>(
    `/settings/additional-tables/${tableId}/fields`,
  );
  return res.data.fields;
}

/**
 * Slugi pól rodzica, które wskazują wiązania `parent_field` z mapy wiązań JEDNEGO pola dla
 * danego klucza (`'default'` albo klasa dokumentu). Posortowane i bez duplikatów — wynik trafia
 * do klucza zapytania, więc musi być deterministyczny. Wiązanie bez sluga dla tego klucza
 * nie wnosi niczego (serwer nie spada na inny klucz).
 */
export function collectParentFieldSlugs(
  bindings: DictionaryParamBindingsMap | null | undefined,
  parentKey: ParentFieldBindingKey,
): string[] {
  const slugs = new Set<string>();
  for (const raw of Object.values(bindings ?? {})) {
    const binding = normalizeDictionaryParamBinding(raw);
    if (binding?.kind !== 'parent_field') continue;
    const slug = binding.fieldSlugs[parentKey];
    if (typeof slug === 'string' && slug.length > 0) slugs.add(slug);
  }
  return [...slugs].sort();
}

/** Suma slugów pola rodzica po wszystkich polach (`collectParentFieldSlugs` per pole). */
export function collectParentFieldSlugsForFields(
  fields: ReadonlyArray<Pick<AdditionalField, 'dictionaryParamBindings'>>,
  parentKey: ParentFieldBindingKey,
): string[] {
  const slugs = new Set<string>();
  for (const field of fields) {
    for (const slug of collectParentFieldSlugs(field.dictionaryParamBindings, parentKey)) {
      slugs.add(slug);
    }
  }
  return [...slugs].sort();
}

/**
 * Wybiera z wartości rodzica (`ownerValues`, slug → wartość z ekranu) wyłącznie `slugs` i
 * koercjuje je do skalarów — wartość nieskalarna albo nieobecna na ekranie → `null`.
 */
export function pickParentValues(
  ownerValues: Readonly<Record<string, unknown>>,
  slugs: readonly string[],
): AdditionalParentValues {
  const picked: AdditionalParentValues = {};
  for (const slug of slugs) {
    picked[slug] = toAdditionalViewRecordValue(ownerValues[slug]);
  }
  return picked;
}
